import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react'

export const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: undefined,
        height: undefined,
    })

    useEffect(() => {
        function handleResize() {
            // Set window width/height to state
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })
        }

        // Add event listener
        window.addEventListener('resize', handleResize)

        // Call handler right away so state gets updated with initial window size
        handleResize()

        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', handleResize)
    }, []) // Empty array ensures that effect is only run on mount

    return windowSize
}

export const useLockBodyScroll = () => {
    useLayoutEffect(() => {
        // Get original body overflow
        const originalStyle = window.getComputedStyle(document.body).overflow
        // Prevent scrolling on mount
        document.body.style.overflow = 'hidden auto'
        // Re-enable scrolling when component unmounts
        return () => (document.body.style.overflow = originalStyle)
    }, []) // Empty array ensures effect is only run on mount and unmount
}

export const useIsMounted = () => {
    const isMounted = useRef(false)
    useEffect(() => {
        isMounted.current = true
        return () => (isMounted.current = false)
    }, [])
    return isMounted
}

/**
 * Returns the content of all script tags
 * in the provided html and the html
 *
 * @param {string} html
 * @returns {[string[], string]} an array of script contents and the html without the scripts
 */
export const useInlineScriptsFromHTML = html => {
    const scripts = useMemo(() => {
        const scripts = getScriptContentFromHTML(html)
        if (scripts.length > 0) {
            removeScriptsFromHtml(html)
        }

        return scripts
    }, [html])

    return [scripts, html]
}

/**
 * Get the content of all script tags in the provided html.
 *
 * @param {string} html
 * @returns {string[]} an array of script contents
 */
function getScriptContentFromHTML(html) {
    const scriptsWithTags = html.match(/<script.*?<\/script>/g)

    if (!scriptsWithTags) {
        return []
    }

    return scriptsWithTags.map(script => script.replace(/<\/?script>/g, ''))
}

/**
 * Remove all script tags from the provided html.
 *
 * @param {string} html
 * @returns string
 */
function removeScriptsFromHtml(html) {
    return html.replace(/<script.*?<\/script>/g, '')
}
